import React from 'react';
import styled from 'styled-components';
import { CloseButton, ModalOverlay } from '../../Styles/AppStyles';

const COLORS = {
  teal: '#0098aa',
  darkTeal: '#007a8a',
  red: '#e05252',
  darkRed: '#c03a3a',
  gray: '#666666',
  lightGray: '#f0f0f0',
  white: '#ffffff',
};

type Variant = 'danger' | 'primary';
type IconVariant = 'delete' | 'restore';

const variantColors = (variant: Variant) => {
  if (variant === 'primary') {
    return {
      accent: COLORS.teal,
      accentDark: COLORS.darkTeal,
      iconBg: '#e6f6f8',
    };
  }
  return {
    accent: COLORS.red,
    accentDark: COLORS.darkRed,
    iconBg: '#fdecea',
  };
};

const ModalContent = styled.div<{ $variant: Variant }>`
  background: ${COLORS.white};
  border-radius: 12px;
  padding: 32px 36px 28px;
  width: 100%;
  max-width: 420px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
  border-top: 4px solid ${({ $variant }) => variantColors($variant).accent};
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;

  @media (max-width: 480px) {
    padding: 24px 16px 20px;
    max-width: 100%;
  }
`;

const IconCircle = styled.div<{ $variant: Variant }>`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background-color: ${({ $variant }) => variantColors($variant).iconBg};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 4px;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 1.2em;
  font-weight: 700;
  color: #222;
  text-align: center;
`;

const Message = styled.p`
  margin: 0;
  font-size: 0.95em;
  color: ${COLORS.gray};
  text-align: center;
  line-height: 1.5;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 8px;
  width: 100%;

  @media (max-width: 480px) {
    flex-direction: column-reverse;
  }
`;

const CancelBtn = styled.button`
  flex: 1;
  padding: 10px 0;
  background-color: ${COLORS.lightGray};
  color: ${COLORS.gray};
  border: none;
  border-radius: 6px;
  font-size: 0.95em;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #ddd;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ConfirmBtn = styled.button<{ $variant: Variant }>`
  flex: 1;
  padding: 10px 0;
  background-color: ${({ $variant }) => variantColors($variant).accent};
  color: ${COLORS.white};
  border: none;
  border-radius: 6px;
  font-size: 0.95em;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${({ $variant }) => variantColors($variant).accentDark};
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

type ConfirmationModalProps = {
  title?: string;
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
  variant?: Variant;
  confirmText?: string;
  isLoading?: boolean;
  loadingText?: string;
  icon?: IconVariant;
};

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  title = 'Are you sure?',
  message,
  onConfirm,
  onCancel,
  variant = 'danger',
  confirmText,
  isLoading = false,
  loadingText = 'Working...',
  icon = 'delete',
}) => {
  const colors = variantColors(variant);
  const resolvedConfirmText = confirmText ?? (variant === 'primary' ? 'Confirm' : 'Delete');
  const resolvedLoadingText = loadingText || 'Working...';

  return (
    <ModalOverlay onClick={isLoading ? undefined : onCancel}>
      <ModalContent $variant={variant} onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={onCancel} disabled={isLoading}>✕</CloseButton>
        <IconCircle $variant={variant}>
          {icon === 'restore' ? (
            <svg
              width="26"
              height="26"
              viewBox="0 0 24 24"
              fill="none"
              stroke={colors.accent}
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3 12a9 9 0 1 0 3-6.7" />
              <polyline points="3 3 3 9 9 9" />
              <path d="M12 7v5l3 3" />
            </svg>
          ) : (
            <svg
              width="26"
              height="26"
              viewBox="0 0 24 24"
              fill="none"
              stroke={colors.accent}
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
              <path d="M10 11v6M14 11v6" />
              <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
            </svg>
          )}
        </IconCircle>
        <Title>{title}</Title>
        <Message>{message}</Message>
        <ButtonRow>
          <CancelBtn onClick={onCancel} disabled={isLoading}>
            Cancel
          </CancelBtn>
          <ConfirmBtn
            $variant={variant}
            onClick={isLoading ? undefined : onConfirm}
            disabled={isLoading}
            aria-busy={isLoading}
          >
            {isLoading ? resolvedLoadingText : resolvedConfirmText}
          </ConfirmBtn>
        </ButtonRow>
      </ModalContent>
    </ModalOverlay>
  );
};

export default ConfirmationModal;
